import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clientAPI, authAPI, adminAPI, notifyAPI } from "./axiosInstance";
import { logout } from "../redux/actions/authActions";
import { persistor } from "../redux/store";

const decodePayload = (jwtToken) => {
  try {
    const payload = jwtToken.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenExpired = (jwtToken) => {
  const payload = decodePayload(jwtToken);
  if (!payload || !payload.exp) return false;
  const currentEpochSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= currentEpochSeconds;
};

const useAxiosInterceptors = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const hasHandledAuthFailure = useRef(false);

  useEffect(() => {
    const handleAuthFailure = () => {
      if (hasHandledAuthFailure.current) return;
      hasHandledAuthFailure.current = true;
      dispatch(logout());
      persistor.purge();
      window.location.assign("/login");
    };

    const adminInterceptor = adminAPI.interceptors.request.use(
      (config) => {
        if (token) {
          if (isTokenExpired(token)) {
            handleAuthFailure();
            return Promise.reject(new Error("JWT token expired"));
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const clientInterceptor = clientAPI.interceptors.request.use(
      (config) => {
        if (token) {
          if (isTokenExpired(token)) {
            handleAuthFailure();
            return Promise.reject(new Error("JWT token expired"));
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const authInterceptor = authAPI.interceptors.request.use(
      (config) => {
        if (token) {
          if (isTokenExpired(token)) {
            handleAuthFailure();
            return Promise.reject(new Error("JWT token expired"));
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const notifyInterceptor = notifyAPI.interceptors.request.use(
      (config) => {
        if (token) {
          if (isTokenExpired(token)) {
            handleAuthFailure();
            return Promise.reject(new Error("JWT token expired"));
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const createResponseErrorHandler = () => (error) => {
      const statusCode = error?.response?.status;
      if (token && (statusCode === 401 || statusCode === 403)) {
        handleAuthFailure();
      }
      return Promise.reject(error);
    };

    const adminResponseInterceptor = adminAPI.interceptors.response.use(
      (response) => response,
      createResponseErrorHandler()
    );

    const clientResponseInterceptor = clientAPI.interceptors.response.use(
      (response) => response,
      createResponseErrorHandler()
    );

    const authResponseInterceptor = authAPI.interceptors.response.use(
      (response) => response,
      createResponseErrorHandler()
    );

    const notifyResponseInterceptor = notifyAPI.interceptors.response.use(
      (response) => response,
      createResponseErrorHandler()
    );

    return () => {
      adminAPI.interceptors.request.eject(adminInterceptor);
      clientAPI.interceptors.request.eject(clientInterceptor);
      authAPI.interceptors.request.eject(authInterceptor);
      notifyAPI.interceptors.request.eject(notifyInterceptor);
      adminAPI.interceptors.response.eject(adminResponseInterceptor);
      clientAPI.interceptors.response.eject(clientResponseInterceptor);
      authAPI.interceptors.response.eject(authResponseInterceptor);
      notifyAPI.interceptors.response.eject(notifyResponseInterceptor);
      hasHandledAuthFailure.current = false;
    };
  }, [dispatch, token]);
};

export default useAxiosInterceptors;